import { useEffect, useRef, useState } from "react";
import { Button, Group, Modal, Stack, Text } from "@mantine/core";
import { guidePopupReady, popupGuides, takeGuidePopup } from "@/lib/guides";
import { useMyGuides } from "@/hooks/use-guides";
import { plainClick, useGuideModal } from "@/hooks/use-guide-modal";
import { useScreenTaken } from "@/hooks/use-screen-taken";
import { GuidePopupList } from "./guide-popup-list";

/**
 * Once per login (#436), a popup with every unfinished guide the member can
 * do — the ones the office left on "Pop up for members". It is decided once
 * per login (`takeGuidePopup`) when `guidePopupReady` allows: on guides fetched
 * after mount (never a previous session's cache), once the announcements and
 * the emergency state are known, while no fullscreen announcement, takeover
 * or emergency is showing, and no guide is open.
 * Reloads, other tabs and token refreshes don't bring it back; the next login
 * does.
 *
 * Choosing a guide closes the popup first and opens the guide modal once the
 * popup is gone — never two modals at once. While open, the list follows live
 * progress; when nothing is left it closes for good, and it steps aside if a
 * takeover or emergency arrives.
 */
export function GuidePopup() {
  const { data, isFetchedAfterMount } = useMyGuides(true, { refetchOnMount: "always" });
  const modal = useGuideModal();
  const decided = useRef(false);
  const [open, setOpen] = useState(false);
  const pendingGuide = useRef<string | null>(null);

  const screen = useScreenTaken();
  const ready = guidePopupReady({
    guidesFetchedAfterMount: isFetchedAfterMount,
    announcementsLoaded: screen.announcementsLoaded,
    emergencyLoaded: screen.emergencyLoaded,
    visibleAnnouncements: screen.visibleAnnouncements,
    emergencyActive: screen.emergencyActive,
    guideOpen: !!modal?.openKey,
  });

  useEffect(() => {
    if (!data || decided.current || !ready) return;
    decided.current = true;
    if (takeGuidePopup(data).length > 0) setOpen(true);
  }, [data, ready]);

  const guides = data ? popupGuides(data.guides, data.is_company_admin) : [];
  const close = () => setOpen(false);
  // Emptied while open (every guide finished or skipped): closed for the rest
  // of the login, so a guide reopening later doesn't pop it up mid-session.
  const empty = guides.length === 0;
  useEffect(() => {
    if (open && empty) setOpen(false);
  }, [open, empty]);

  // A plain click opens the guide modal once the popup has closed (never two
  // modals at once); a ctrl/middle click keeps the link (a new tab).
  const onOpen = (key: string, e: React.MouseEvent) => {
    if (modal && plainClick(e)) {
      e.preventDefault();
      pendingGuide.current = key;
    }
    close();
  };

  return (
    <Modal
      opened={open && !empty && !screen.covered}
      onClose={close}
      onExitTransitionEnd={() => {
        const key = pendingGuide.current;
        pendingGuide.current = null;
        if (key) modal?.openGuide(key);
      }}
      title={<Text fw={700}>Setup guides for you</Text>}
      size="lg"
      centered
    >
      <Stack gap="md">
        <Text fz="sm" c="dimmed">
          {guides.length === 1 ? "There is a setup guide" : `There are ${guides.length} setup guides`} that you
          have not finished. Pick one to continue. You can find all guides in the Office Guide.
        </Text>
        <GuidePopupList guides={guides} onOpen={onOpen} />
        <Group justify="flex-end">
          <Button variant="default" onClick={close}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
